// Every closed list of values (data-model.md Section 11, DM-4; architecture/architecture.md Section 8).
// The database is the master copy: each list is a lookup table seeded from these values. The code
// depends on the values, so a new value ships with the code that handles it, and needs no schema change.
// S-2's three MCP lists take their values from the MCP constants, so each list is stated once.
//
// Keys are the list names used in data-model.md. The lookup table for a list is its plural
// (see LOOKUP_TABLE_NAMES in the server schema).
import { MCP_CLIENT_IDENTIFICATIONS, MCP_CLIENT_KINDS } from './mcp-clients.js';
import { MCP_CONNECTION_END_REASONS } from './mcp-connections.js';

export const CODE_LISTS = {
  // ai_coding_agent is legacy (poc CT-007): old history may name such an actor; nothing creates one any more.
  // mcp_app (poc CT-014): an app MCP client acting as itself through MCP, one per app client (mcp_clients.principal_id).
  principal_kind: ['person', 'ai_coding_agent', 'platform_admins', 'system', 'triage_llm', 'anonymised', 'mcp_app'],
  account_state: ['active', 'disabled'],
  // all_staff (a reporting-only group) was retired by CT-002: reporting needs no group. poc CT-008: exactly
  // two groups, Admins (platform_admin) and Editors; the per-app `access` groups are retired (migration 0008).
  security_group_purpose: ['platform_admin', 'editors'],
  role_scope: ['tool', 'app'],
  widget_state: ['on', 'off'],
  // visibility_mode (who an environment showed the tab to) was retired by CT-026 (migration 0023): the drawer
  // follows the site's own access.
  masking_rule_kind: ['page_element', 'pattern', 'context_key'],
  setting_level: ['tool', 'app', 'environment'],
  setting_value_type: ['boolean', 'integer', 'decimal', 'duration', 'time_of_day', 'text', 'choice'],
  stage_code: ['triage', 'review', 'ready', 'in_progress', 'awaiting_confirmation', 'done', 'not_actioned'],
  workflow_version_state: ['draft', 'published'],
  report_link_kind: ['origin', 'duplicate', 'merged'],
  install_channel: ['snippet', 'sdk', 'extension'],
  identity_source: ['tool_session', 'sdk_identity', 'drawer_sign_in'],
  device_type: ['desktop', 'tablet', 'phone'],
  orientation: ['portrait', 'landscape'],
  follow_up_outcome: ['pending', 'answered', 'skipped', 'unanswered'],
  // grabbed_screenshot (migration 0019, REQ-10.8): screenshots the reporter grabbed that were left out because
  // masking failed, recorded on the report so staff know they existed (module-7 3.4).
  capture_part: ['environment_details', 'console', 'network', 'app_context', 'submit_screenshot', 'grabbed_screenshot'],
  capture_part_state: ['captured', 'failed', 'left_out_masking_failed', 'truncated'],
  console_level: ['error', 'warn', 'info', 'log', 'debug'],
  attachment_kind: ['grabbed_screenshot', 'submit_screenshot', 'reporter_upload', 'staff_file', 'reply_file'],
  attachment_masking: ['masked', 'not_masked', 'not_applicable'],
  rendition_kind: ['original', 'with_markup', 'thumbnail'],
  removal_kind: ['retention', 'admin_delete'],
  description_section: ['doing', 'happened', 'where', 'expected', 'steps_to_reproduce'],
  closure_kind: ['confirmed', 'auto_closed', 'merged', 'shipped'],
  flag_type: ['awaiting_reporter', 'not_triaged_by_ai'],
  // linear: the System mirroring a ticket's Linear issue, and recording the hand-off (poc CT-009, module-14)
  channel: ['staff_pages', 'dashboard', 'drawer', 'reporter_pages', 'mcp', 'triage', 'automation', 'linear'],
  comment_kind: ['comment', 'reporter_reply'],
  comment_visibility: ['internal', 'reporter_visible'],
  change_kind: [
    'created',
    'triaged',
    'fields_changed',
    'moved',
    'assigned',
    'unassigned',
    'report_linked',
    'report_unlinked',
    'merged',
    'comment_added',
    'attachment_added',
    'flag_set',
    'flag_cleared',
    'marked_fixed',
    'confirmed',
    'reopened',
    'report_withdrawn',
    'auto_closed',
    'responsibility_passed',
    'anonymised',
    // poc CT-009: the ticket's Linear issue was created (module-14 Section 3). A history entry; no status change.
    'handed_off',
  ],
  ticket_field: [
    'title',
    'description',
    'type',
    'suggested_priority',
    'priority',
    'status',
    'responsible_owner',
    'assignee',
    'app',
    'duplicate_links',
    'flags',
    'risk_result',
  ],
  audience_kind: [
    'assignee',
    'responsible_owner',
    // Nothing emits app_owners since poc CT-008 (ticket notifications go to the responsible editor); old
    // event_audiences rows keep it and still resolve (to the app's fallback owner, or the Admins).
    'app_owners',
    'linked_reporters',
    'reporter',
    'mentioned_person',
    'platform_admins',
    // One named person who isn't one of the roles above, e.g. a daily digest's recipient (M6, REQ-18.4).
    'person',
  ],
  notification_mode: ['immediate', 'digest', 'off'],
  dispatch_state: ['pending', 'done', 'failed'],
  delivery_state: ['pending', 'delivered', 'retrying', 'kept_for_later', 'skipped'],
  // The AI tasks an LLM in Admin > AI platforms is used for. CT-024 (migration 0022): mcp_client went; MCP clients run
  // on their own AI, never on one of the tool's LLMs.
  ai_use: ['triage'],
  triage_mode: ['in_session', 'deferred'],
  triage_outcome: ['succeeded', 'llm_unavailable', 'timed_out', 'invalid_output', 'error'],
  risk_result: ['low_risk', 'not_low_risk'],
  risk_verdict: ['correct', 'incorrect'],
  review_reason_scope: ['risk_decision'],
  report_kind: ['risk_decisions'],
  report_trigger: ['scheduled', 'on_demand'],
  removal_subject: ['ticket', 'attachment'],
  removal_action: [
    'deleted_by_admin',
    'attachments_removed_by_retention',
    'ticket_deleted_by_retention',
    'ticket_anonymised_by_retention',
  ],
  export_kind: ['person_data', 'oversight_items'],
  export_format: ['csv', 'json', 'archive'],
  config_subject: [
    'app',
    'app_key',
    'environment',
    'web_address',
    // History only since poc CT-008 (per-app role groups are gone); old configuration_changes rows keep it.
    'app_role_group',
    'fallback_owner',
    'routing_responsibility',
    'masking_rule',
    'masking_check',
    'setting',
    'security_group',
    'workflow_version',
    'ai_platform',
    'mcp_client',
    'triage_policy_version',
    // POC addition (architecture Section 8): OAuth redirect URIs of registered MCP clients
    'mcp_client_redirect_uri',
    // POC additions (architecture Section 8): the reference lists Platform admins edit in Admin > Lists
    'priority',
    'data_classification',
    'masking_pattern',
    'review_reason',
    // poc CT-009 (module-14 Section 2): the Linear team and project an app's tickets are handed to
    'app_linear_target',
    // CT-028 (module-4 2a): a place found on an app's pages, switched between hidden and allowed, or renamed
    'masking_finding',
  ],
  config_action: ['created', 'updated', 'deleted', 'published'],
  // POC additions (architecture Section 8, 7.2): how a group membership sync ran, and how it ended
  group_sync_path: ['sign_in', 'admin_sync', 'scheduled'],
  group_sync_outcome: ['succeeded', 'failed', 'permission_missing'],
  // POC addition (poc CT-005, architecture 7.12): the kind of report the reporter picked in the drawer. poc CT-019
  // added data_issue and wording_or_help (both set the ticket type bug); the order is the drawer's (reported-types.ts).
  reported_type: ['bug', 'data_issue', 'wording_or_help', 'feature_request', 'not_sure'],
  // POC addition (poc CT-020, architecture 7.12): a report from a drawer that shows "What we'll send" before anything
  // is filed. in_review: saved but unfiled (the AI prepares its version, the reporter checks it), invisible to staff;
  // sent: the reporter pressed Send, and it's filed like any report. An older client's report has none (filed at once).
  report_review_state: ['in_review', 'sent'],
  // S-2 (migration 0018): the MCP lists that were CHECK constraints, now lookup tables like every other list.
  mcp_client_kind: MCP_CLIENT_KINDS,
  mcp_client_identification: MCP_CLIENT_IDENTIFICATIONS,
  mcp_connection_end_reason: MCP_CONNECTION_END_REASONS,
} as const;

/**
 * Values poc CT-007 retired (with the AI coding agent and the old pick-up step). Nothing writes them any
 * more, but append-only rows on an old database may still hold them (migration 0007 deletes each lookup row
 * nothing references), so the READ contracts accept them alongside the current list. Writers use CODE_LISTS.
 */
export const LEGACY_CODES = {
  change_kind: ['claimed', 'claim_released', 'claim_lapsed'],
  ticket_field: ['claim'],
  comment_kind: ['progress_note'],
  flag_type: ['agent_claim_overdue'],
} as const satisfies Partial<Record<keyof typeof CODE_LISTS, readonly string[]>>;

export type CodeListName = keyof typeof CODE_LISTS;
export type CodeOf<L extends CodeListName> = (typeof CODE_LISTS)[L][number];

export const PRINCIPAL_KINDS = CODE_LISTS.principal_kind;
export type PrincipalKind = CodeOf<'principal_kind'>;
/** Kinds with exactly one row each (data-model.md 4.1, unique index on principals.kind). */
export const SINGLETON_PRINCIPAL_KINDS = ['platform_admins', 'system', 'triage_llm', 'anonymised'] as const;
export type SingletonPrincipalKind = (typeof SINGLETON_PRINCIPAL_KINDS)[number];

export type AccountState = CodeOf<'account_state'>;
export type SecurityGroupPurpose = CodeOf<'security_group_purpose'>;
export type RoleScope = CodeOf<'role_scope'>;
export type WidgetState = CodeOf<'widget_state'>;
export type MaskingRuleKind = CodeOf<'masking_rule_kind'>;
export type SettingLevel = CodeOf<'setting_level'>;
export type SettingValueType = CodeOf<'setting_value_type'>;
export type StageCode = CodeOf<'stage_code'>;
export type WorkflowVersionState = CodeOf<'workflow_version_state'>;
export type ReportLinkKind = CodeOf<'report_link_kind'>;
export type InstallChannel = CodeOf<'install_channel'>;
export type IdentitySource = CodeOf<'identity_source'>;
export type DeviceType = CodeOf<'device_type'>;
export type Orientation = CodeOf<'orientation'>;
export type FollowUpOutcome = CodeOf<'follow_up_outcome'>;
export type CapturePart = CodeOf<'capture_part'>;
export type CapturePartState = CodeOf<'capture_part_state'>;
export type ConsoleLevel = CodeOf<'console_level'>;
export type AttachmentKind = CodeOf<'attachment_kind'>;
export type AttachmentMasking = CodeOf<'attachment_masking'>;
export type RenditionKind = CodeOf<'rendition_kind'>;
export type RemovalKind = CodeOf<'removal_kind'>;
export type DescriptionSection = CodeOf<'description_section'>;
export type ClosureKind = CodeOf<'closure_kind'>;
export type FlagType = CodeOf<'flag_type'>;
export const CHANNELS = CODE_LISTS.channel;
export type Channel = CodeOf<'channel'>;
export type CommentKind = CodeOf<'comment_kind'>;
export type CommentVisibility = CodeOf<'comment_visibility'>;
export const CHANGE_KINDS = CODE_LISTS.change_kind;
export type ChangeKind = CodeOf<'change_kind'>;
export type TicketField = CodeOf<'ticket_field'>;
export const AUDIENCE_KINDS = CODE_LISTS.audience_kind;
export type AudienceKind = CodeOf<'audience_kind'>;
export type NotificationMode = CodeOf<'notification_mode'>;
export type DispatchState = CodeOf<'dispatch_state'>;
export type DeliveryState = CodeOf<'delivery_state'>;
export type AiUse = CodeOf<'ai_use'>;
export type TriageMode = CodeOf<'triage_mode'>;
export type TriageOutcome = CodeOf<'triage_outcome'>;
export type RiskResult = CodeOf<'risk_result'>;
export type RiskVerdict = CodeOf<'risk_verdict'>;
export type ReviewReasonScope = CodeOf<'review_reason_scope'>;
export type ReportKind = CodeOf<'report_kind'>;
export type ReportTrigger = CodeOf<'report_trigger'>;
export type RemovalSubject = CodeOf<'removal_subject'>;
export type RemovalAction = CodeOf<'removal_action'>;
export type ExportKind = CodeOf<'export_kind'>;
export type ExportFormat = CodeOf<'export_format'>;
export type ConfigSubject = CodeOf<'config_subject'>;
export type ConfigAction = CodeOf<'config_action'>;
export type GroupSyncPath = CodeOf<'group_sync_path'>;
export type GroupSyncOutcome = CodeOf<'group_sync_outcome'>;
export type ReportedType = CodeOf<'reported_type'>;
export type ReportReviewState = CodeOf<'report_review_state'>;
